import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, vModelSelect as _vModelSelect, withDirectives as _withDirectives, unref as _unref, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "formulario-modelo" }
const _hoisted_2 = {
  key: 0,
  class: "marco"
}
const _hoisted_3 = { class: "encabezado-flex" }
const _hoisted_4 = {
  key: 0,
  class: "ctx-modelo"
}
const _hoisted_5 = { class: "acciones-encabezado" }
const _hoisted_6 = ["disabled"]
const _hoisted_7 = { class: "filtro-botones" }
const _hoisted_8 = { class: "decimales-control" }
const _hoisted_9 = ["disabled"]
const _hoisted_10 = { class: "decimales-valor" }
const _hoisted_11 = ["disabled"]
const _hoisted_12 = {
  key: 0,
  class: "filtro-select"
}
const _hoisted_13 = { for: "componente-filtro" }
const _hoisted_14 = ["value"]
const _hoisted_15 = {
  key: 1,
  class: "filtro-select"
}
const _hoisted_16 = { for: "componente-n2-filtro" }
const _hoisted_17 = ["value"]
const _hoisted_18 = { class: "lista-componentes" }
const _hoisted_19 = { class: "acciones-encabezado" }
const _hoisted_20 = ["disabled"]

import { ref, computed, onMounted, provide } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useComponentes } from '@/store/GestionComponentes'
import { useModelo } from '@/store/GestionModelo'
import type Componente from '@/interfaces/Componente'
import LoadingOverlay from '@/components/LoadingOverlay.vue'
import { shallowRef } from 'vue'
import NodoComponente from '@/components/NodoComponente.vue'
import MensajeToast from '@/components/MensajeToast.vue'
import { limpiarNiveles } from '@/utils/nivelesHelper'
import { sumaColapsada, contarSlots } from '@/utils/pesosGrupo'

interface ComponenteAplanado {
  ref: Componente 
  nivel: number
}


export default /*@__PURE__*/_defineComponent({
  __name: 'GestionPesosView',
  setup(__props) {

/**
 * Reparto de pesos entre hermanos del árbol.
 *
 * Los pesos se autodistribuyen mientras `auto_distribucion` esté activo; fijar
 * uno a mano lo desactiva para ese grupo. Las variantes que comparten
 * `grupo_variante` ocupan UN solo slot de peso.
 *
 * ── Enrutado ─────────────────────────────────────────────────────
 *   `/gestionPesos/:id_modelo` (Pesos) — permiso `modelos.editar`
 *
 * Vista enrutada. Obtiene sus datos a través de stores Pinia: no llama a `http`
 * ni a los servicios directamente.
 */

const toastRef = ref<InstanceType<typeof MensajeToast> | null>(null)
const ruta = useRoute()
const router = useRouter()
const storeComponentes = useComponentes()
const modeloStore = useModelo()

const componentes = ref<ComponenteAplanado[]>([])
const idModelo = Number(ruta.params.id_modelo)
const nombreModelo = ref('')
const loading = ref(true)
const lockedMap = shallowRef<Map<number, boolean>>(new Map())

// Estados del filtro
const tipoFiltro = ref<'todos' | 'solo-nivel-1' | 'nivel-1-y-2' | 'por-componente' | 'por-componente-n2'>('todos')
const componenteNivel1Filtro = ref<number | null>(null)
const componenteNivel2Filtro = ref<number | null>(null)
const nivelesArray = ref<string[]>([])
const decimales = ref(2)

// Función para obtener el nombre del nivel según su índice
function obtenerNombreNivel(nivelIndex: number): string {
  if (nivelIndex <= 0 || nivelIndex > nivelesArray.value.length) return ''
  return nivelesArray.value[nivelIndex - 1] ?? ''
}

// Proporcionar la función a través de inyección para los componentes hijos
provide('obtenerNombreNivel', obtenerNombreNivel)
provide('decimales', decimales)

// Marcar componente como modificado por usuario (congela su peso)
function marcarComoModificado(id?: number) {
  if (id == null) return
  const newMap = new Map(lockedMap.value)
  newMap.set(id, true)
  lockedMap.value = newMap
}

function estaModificado(id?: number): boolean {
  if (id == null) return false
  return !!lockedMap.value.get(id)
}

function onUpdatePeso({ comp, valor }: { comp: Componente; valor: number }) {
  comp.peso = valor
}

// Obtener todos los componentes de nivel 1 (directos del raíz)
function obtenerComponentesNivel1(): Componente[] {
  const raiz = storeComponentes.componentes[0]
  return [...(raiz?.children ?? [])].sort((a, b) =>
    (a.ordinal ?? '').localeCompare(b.ordinal ?? '', undefined, { numeric: true })
  )
}

// Obtener solo componentes de nivel 1 sin mostrar sus hijos
function obtenerComponentesNivel1SinHijos(): Componente[] {
  const raiz = storeComponentes.componentes[0]
  return [...(raiz?.children ?? [])].sort((a, b) =>
    (a.ordinal ?? '').localeCompare(b.ordinal ?? '', undefined, { numeric: true })
  )
}

// Obtener un componente con todos sus hijos recursivos
function obtenerComponenteConHijos(componenteId: number): Componente | null {
  function buscar(arbol: Componente[]): Componente | null {
    for (const comp of arbol) {
      if (comp.id_componente === componenteId) {
        return comp
      }
      if (comp.children?.length) {
        const resultado = buscar(comp.children)
        if (resultado) return resultado
      }
    }
    return null
  }

  const raiz = storeComponentes.componentes[0]
  return raiz?.children ? buscar(raiz.children) : null
}

// Obtener el nivel de un componente por su ID
function obtenerNivelComponente(componenteId: number): number {
  const aplanado = componentes.value.find(c => c.ref.id_componente === componenteId)
  return aplanado?.nivel ?? 1
}

function formatOrdinal(ordinal: string): string {
  if (!ordinal) return ''
  const parts = ordinal.split('.')
  return parts.length <= 1 ? ordinal : parts.slice(1).join('.')
}

function precisionSignificativa(peso: number): number {
  for (let d = 0; d <= 4; d++) {
    if (Math.abs(peso - Number(peso.toFixed(d))) < 1e-9) return d
  }
  return 4
}

function detectarPrecisionArbol(nodos: Componente[]): number {
  let max = 0
  const scan = (c: Componente): void => {
    if (c.peso != null) max = Math.max(max, precisionSignificativa(c.peso))
    for (const h of c.children ?? []) scan(h)
  }
  for (const c of nodos) scan(c)
  return max
}

onMounted(async () => {
  try {
    loading.value = true
    if (!Number.isFinite(idModelo) || idModelo <= 0) {
      alert('ID de modelo inválido')
      return
    }

    const [raiz, modelo] = await Promise.all([
      storeComponentes.cargarRaizPorModelo(idModelo),
      modeloStore.getModeloById(idModelo)
    ])

    if (!raiz || !raiz.id_componente) {
      alert('No se encontró componente raíz para este modelo')
      return
    }

    nombreModelo.value = modelo?.nombre ?? ''
    if (modelo?.niveles) nivelesArray.value = limpiarNiveles(modelo.niveles)

    const ok = await storeComponentes.cargarArbolDesdeRaiz(raiz.id_componente)
    if (!ok) {
      alert('Error al cargar árbol de componentes')
      return
    }

    componentes.value = aplanarComponentes(storeComponentes.componentes)

    const raizNodo = storeComponentes.componentes[0]
    if (raizNodo) decimales.value = detectarPrecisionArbol(raizNodo.children ?? [])
  } finally {
    loading.value = false
  }
})

const hijosRaiz = computed(() => {
  if (tipoFiltro.value === 'solo-nivel-1') {
    return obtenerComponentesNivel1SinHijos()
  }
  if (tipoFiltro.value === 'por-componente' && componenteNivel1Filtro.value != null) {
    const comp = obtenerComponenteConHijos(componenteNivel1Filtro.value)
    return comp ? [comp] : []
  }
  if (tipoFiltro.value === 'por-componente-n2' && componenteNivel2Filtro.value != null) {
    const comp = obtenerComponenteConHijos(componenteNivel2Filtro.value)
    return comp ? [comp] : []
  }
  return obtenerComponentesNivel1()
})

// Computeds para los desplegables de filtro
const componentesNivel1 = computed(() => obtenerComponentesNivel1())

const todosComponentesNivel2 = computed<Componente[]>(() => {
  const raiz = storeComponentes.componentes[0]
  const n2s: Componente[] = []
  for (const n1 of raiz?.children ?? []) {
    for (const n2 of n1.children ?? []) n2s.push(n2)
  }
  return n2s.sort((a, b) =>
    (a.ordinal ?? '').localeCompare(b.ordinal ?? '', undefined, { numeric: true })
  )
})

// Nivel base para los componentes mostrados
const nivelBase = computed(() => {
  if (tipoFiltro.value === 'por-componente' && componenteNivel1Filtro.value != null) {
    return obtenerNivelComponente(componenteNivel1Filtro.value)
  }
  if (tipoFiltro.value === 'por-componente-n2' && componenteNivel2Filtro.value != null) {
    return obtenerNivelComponente(componenteNivel2Filtro.value)
  }
  return 1
})

// Aplanar árbol con referencia directa (excluye nivel 0: raíz invisible)
function aplanarComponentes(arbol: Componente[], nivel = 0): ComponenteAplanado[] {
  const resultado: ComponenteAplanado[] = []
  for (const comp of arbol) {
    if (nivel > 0) {
      resultado.push({ ref: comp, nivel })
    }
    if ((comp.children?.length ?? 0) > 0) {
      resultado.push(...aplanarComponentes(comp.children!, nivel + 1))
    }
  }
  return resultado
}

// Helper: obtener referencia del padre desde el aplanado
function obtenerPadreRef(padreId: number | null | undefined): Componente | null {
  if (padreId == null) return null
  const padreAplanado = componentes.value.find(c => c.ref.id_componente === padreId)
  return padreAplanado?.ref ?? null
}

// Validaciones de input
function limitarDecimales(componente: Componente) {
  if (componente.peso != null) {
    if (componente.peso < 0) componente.peso = 0
    const partes = componente.peso.toString().split('.')
    if (partes[1]?.length > decimales.value) {
      componente.peso = parseFloat(`${partes[0]}.${partes[1].slice(0, decimales.value)}`)
    }
  }
}

function formatearDosDecimales(componente: Componente) {
  if (componente.peso != null && !isNaN(componente.peso)) {
    componente.peso = parseFloat(componente.peso.toFixed(decimales.value))
  }
}

// Redistribuir hijos respetando cuáles ya fueron modificados por el usuario
function redistribuirHijos(padre: Componente) {
  const hijos = padre.children ?? []
  if (!hijos.length) return
  const total = padre.padre_id == null ? 100 : (padre.peso ?? 0)

  // Separar hijos modificados (congelados) y libres
  const modificados = hijos.filter(h => estaModificado(h.id_componente))
  const libres = hijos.filter(h => !estaModificado(h.id_componente))

  // Calcular suma de modificados (variantes comparten slot)
  const sumaModificados = sumaColapsada(modificados)
  const restante = Math.max(0, total - sumaModificados)

  // Redistribuir entre libres: cada variante recibe el slot COMPLETO (no se divide
  // entre variantes), los hijos normales reciben una parte igual.
  if (libres.length > 0) {
    const numSlots = contarSlots(libres)
    if (numSlots > 0) {
      const nuevoPeso = parseFloat((restante / numSlots).toFixed(decimales.value))
      libres.forEach(h => {
        h.peso = nuevoPeso
        redistribuirHijos(h)
      })
    }
  }

  // Los modificados mantienen su peso, pero sus hijos se redistribuyen
  modificados.forEach(h => {
    redistribuirHijos(h)
  })
}

// Obtener numeración jerárquica (1, 1.1, 1.1.1, etc.)
function obtenerNumeracionJerarquica(componente: Componente, nivel: number): string {
  const aplanado = componentes.value.find(c => c.ref.id_componente === componente.id_componente)
  if (!aplanado) return ''
  
  const numeros: number[] = []
  let actual: ComponenteAplanado | null = aplanado
  
  // Construir la ruta desde el componente actual hasta la raíz
  while (actual && actual.nivel > 0) {
    const padre = obtenerPadreRef(actual.ref.padre_id)
    if (!padre) {
      // Encontrar posición entre hermanos en nivel 1
      const hermanos = componentes.value.filter(c => c.nivel === 1)
      const hermanosSorted = hermanos.sort((a, b) => 
        (a.ref.ordinal ?? '').localeCompare(b.ref.ordinal ?? '', undefined, { numeric: true })
      )
      const posicion = hermanosSorted.findIndex(h => h.ref.id_componente === actual!.ref.id_componente)
      numeros.unshift(posicion + 1)
      break
    } else {
      // Encontrar posición entre hermanos
      const hermanos = padre.children ?? []
      const hermanosSorted = [...hermanos].sort((a, b) => 
        (a.ordinal ?? '').localeCompare(b.ordinal ?? '', undefined, { numeric: true })
      )
      const posicion = hermanosSorted.findIndex(h => h.id_componente === actual!.ref.id_componente)
      numeros.unshift(posicion + 1)
      
      // Pasar al padre
      const actualAplanado = componentes.value.find(c => c.ref.id_componente === padre.id_componente)
      actual = actualAplanado ?? null
    }
  }
  
  return numeros.join('.')
}

// Evento principal de cambio
function redistribuirPeso(aplanado: ComponenteAplanado) {
  const nodo = aplanado.ref

  // Marcar este componente como modificado por el usuario
  marcarComoModificado(nodo.id_componente)

  // Si tiene hijos, redistribuye entre ellos respetando modificaciones
  if (nodo.children && nodo.children.length > 0) {
    redistribuirHijos(nodo)
  }

  // Redistribuir entre hermanos en el mismo nivel respetando modificaciones
  redistribuirEntreHermanos(aplanado)
}

// Redistribuir entre hermanos respetando cuáles fueron modificados
function redistribuirEntreHermanos(aplanado: ComponenteAplanado) {
  const nivel = aplanado.nivel
  const padreId = aplanado.ref.padre_id ?? null

  // Obtener hermanos en el mismo nivel
  const hermanosAplanados = componentes.value.filter(c =>
    c.nivel === nivel && (c.ref.padre_id ?? null) === padreId
  )

  if (hermanosAplanados.length <= 1) return

  // Obtener peso total permitido
  const padreRef = obtenerPadreRef(padreId)
  const totalPermitido = padreRef ? (padreRef.peso ?? 0) : 100

  // Separar hermanos modificados y libres
  const modificados = hermanosAplanados.filter(h => estaModificado(h.ref.id_componente))
  const libres = hermanosAplanados.filter(h => !estaModificado(h.ref.id_componente))

  // Calcular suma de modificados (variantes comparten slot)
  const sumaModificados = sumaColapsada(modificados.map(h => h.ref))
  const restante = Math.max(0, totalPermitido - sumaModificados)

  // Distribuir entre libres tratando el grupo de variantes como un único slot.
  if (libres.length > 0) {
    const numSlots = contarSlots(libres.map(h => h.ref))
    if (numSlots > 0) {
      const nuevoPeso = parseFloat((restante / numSlots).toFixed(decimales.value))
      libres.forEach(h => {
        h.ref.peso = nuevoPeso
        redistribuirHijos(h.ref)
      })
    }
  }
}

function validarPesosArbol(nodos: Componente[], totalEsperado: number): string | null {
  if (!nodos.length) return null
  // Las variantes por enfoque comparten un slot: cuentan una sola vez en la suma.
  const suma = sumaColapsada(nodos)
  const sumaRedondeada = parseFloat(suma.toFixed(decimales.value))
  const diferencia = Math.abs(sumaRedondeada - totalEsperado)
  if (diferencia > 0.5) {
    return `La suma de pesos (${sumaRedondeada}) no coincide con el total esperado (${totalEsperado}).`
  }
  for (const nodo of nodos) {
    if (nodo.children?.length) {
      const error = validarPesosArbol(nodo.children, nodo.peso ?? 0)
      if (error) return error
    }
  }
  return null
}

// Guardar cambios al backend
async function guardarCambios() {
  const arbolCompleto = obtenerComponentesNivel1()
  const errorValidacion = validarPesosArbol(arbolCompleto, 100)
  if (errorValidacion) {
    toastRef.value?.showToast('Los pesos no están correctos, revise la distribución antes de guardar.', 'error')
    return
  }

  try {
    loading.value = true

    // Guardar TODOS los nodos (ingresados y auto-distribuidos), usando los objetos
    // exactos del aplanado local — los mismos que modificaron redistribuirHijos y
    // redistribuirEntreHermanos — excluyendo la raíz que no tiene peso propio.
    const todos = componentes.value.map(a => a.ref)
    const resultados = await Promise.allSettled(todos.map(n => storeComponentes.actualizar(n)))
    const fallidos = resultados.filter(
      r => r.status === 'rejected' || (r.status === 'fulfilled' && r.value === null)
    ).length
    if (fallidos > 0) throw new Error(`${fallidos} componente(s) no pudieron guardarse.`)

    router.push({ name: 'ComponentesModelo', params: { id_modelo: idModelo } })
  } catch (err) {
    toastRef.value?.showToast('Error al guardar los pesos', 'error')
  } finally {
    loading.value = false
  }
}

function volver() {
  router.back()
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(LoadingOverlay, {
      visible: loading.value,
      message: "Cargando pesos..."
    }, null, 8, ["visible"]),
    _createVNode(MensajeToast, {
      ref_key: "toastRef",
      ref: toastRef
    }, null, 512),
    (!loading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", null, [
              _cache[9] || (_cache[9] = _createElementVNode("h2", null, "Gestión de Pesos", -1)),
              (nombreModelo.value)
                ? (_openBlock(), _createElementBlock("p", _hoisted_4, _toDisplayString(nombreModelo.value), 1))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("div", _hoisted_5, [
              _createElementVNode("button", {
                class: "btn salir",
                onClick: volver
              }, "Volver"),
              _createElementVNode("button", {
                class: "btn guardar",
                onClick: guardarCambios,
                disabled: loading.value
              }, _toDisplayString(loading.value ? 'Guardando...' : 'Guardar Cambios'), 9, _hoisted_6)
            ])
          ]),
          _cache[13] || (_cache[13] = _createElementVNode("div", { class: "divisor" }, null, -1)),
          _createElementVNode("div", _hoisted_7, [
            _createElementVNode("button", {
              class: _normalizeClass(['btn-filtro', tipoFiltro.value === 'todos' ? 'activo' : '']),
              onClick: _cache[0] || (_cache[0] = ($event: any) => {tipoFiltro.value = 'todos'; componenteNivel1Filtro.value = null})
            }, " Mostrar todo el modelo ", 2),
            _createElementVNode("button", {
              class: _normalizeClass(['btn-filtro', tipoFiltro.value === 'solo-nivel-1' ? 'activo' : '']),
              onClick: _cache[1] || (_cache[1] = ($event: any) => {tipoFiltro.value = 'solo-nivel-1'; componenteNivel1Filtro.value = null})
            }, " Mostrar solo " + _toDisplayString(nivelesArray.value[0] ?? 'Componentes'), 3),
            (nivelesArray.value.length >= 2)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 0,
                  class: _normalizeClass(['btn-filtro', tipoFiltro.value === 'nivel-1-y-2' ? 'activo' : '']),
                  onClick: _cache[2] || (_cache[2] = ($event: any) => {tipoFiltro.value = 'nivel-1-y-2'; componenteNivel1Filtro.value = null})
                }, _toDisplayString(nivelesArray.value[0] ?? 'Nivel 1') + " y " + _toDisplayString(nivelesArray.value[1] ?? 'Nivel 2'), 3))
              : _createCommentVNode("", true),
            _createElementVNode("button", {
              class: _normalizeClass(['btn-filtro', tipoFiltro.value === 'por-componente' ? 'activo' : '']),
              onClick: _cache[3] || (_cache[3] = ($event: any) => {tipoFiltro.value = 'por-componente'; componenteNivel2Filtro.value = null})
            }, " Por " + _toDisplayString(nivelesArray.value[0] ?? 'Componentes'), 3),
            (todosComponentesNivel2.value.length > 0)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 1,
                  class: _normalizeClass(['btn-filtro', tipoFiltro.value === 'por-componente-n2' ? 'activo' : '']),
                  onClick: _cache[4] || (_cache[4] = ($event: any) => {tipoFiltro.value = 'por-componente-n2'; componenteNivel1Filtro.value = null})
                }, " Por " + _toDisplayString(nivelesArray.value[1] ?? 'Componentes'), 3))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_8, [
              _cache[10] || (_cache[10] = _createElementVNode("span", { class: "decimales-label" }, "Decimales:", -1)),
              _createElementVNode("button", {
                class: "btn-dec",
                disabled: decimales.value <= 0,
                onClick: _cache[5] || (_cache[5] = ($event: any) => (decimales.value = Math.max(0, decimales.value - 1)))
              }, "−", 8, _hoisted_9),
              _createElementVNode("span", _hoisted_10, _toDisplayString(decimales.value), 1),
              _createElementVNode("button", {
                class: "btn-dec",
                disabled: decimales.value >= 4,
                onClick: _cache[6] || (_cache[6] = ($event: any) => (decimales.value = Math.min(4, decimales.value + 1)))
              }, "+", 8, _hoisted_11)
            ])
          ]),
          (tipoFiltro.value === 'por-componente')
            ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                _createElementVNode("label", _hoisted_13, "Selecciona un " + _toDisplayString(nivelesArray.value[0]?.toLowerCase() ?? 'componente') + ":", 1),
                _withDirectives(_createElementVNode("select", {
                  "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((componenteNivel1Filtro).value = $event)),
                  id: "componente-filtro",
                  class: "form-control"
                }, [
                  _cache[11] || (_cache[11] = _createElementVNode("option", { value: null }, "-- Selecciona --", -1)),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(componentesNivel1.value, (comp) => {
                    return (_openBlock(), _createElementBlock("option", {
                      key: comp.id_componente,
                      value: comp.id_componente
                    }, _toDisplayString(formatOrdinal(comp.ordinal ?? '')) + " - " + _toDisplayString(comp.nombre), 9, _hoisted_14))
                  }), 128))
                ], 512), [
                  [
                    _vModelSelect,
                    componenteNivel1Filtro.value,
                    void 0,
                    { number: true }
                  ]
                ])
              ]))
            : _createCommentVNode("", true),
          (tipoFiltro.value === 'por-componente-n2')
            ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                _createElementVNode("label", _hoisted_16, "Selecciona un " + _toDisplayString(nivelesArray.value[1]?.toLowerCase() ?? 'componente') + ":", 1),
                _withDirectives(_createElementVNode("select", {
                  "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((componenteNivel2Filtro).value = $event)),
                  id: "componente-n2-filtro",
                  class: "form-control"
                }, [
                  _cache[12] || (_cache[12] = _createElementVNode("option", { value: null }, "-- Selecciona --", -1)),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(todosComponentesNivel2.value, (comp) => {
                    return (_openBlock(), _createElementBlock("option", {
                      key: comp.id_componente,
                      value: comp.id_componente
                    }, _toDisplayString(formatOrdinal(comp.ordinal ?? '')) + " - " + _toDisplayString(comp.nombre), 9, _hoisted_17))
                  }), 128))
                ], 512), [
                  [
                    _vModelSelect,
                    componenteNivel2Filtro.value,
                    void 0,
                    { number: true }
                  ]
                ])
              ]))
            : _createCommentVNode("", true),
          _cache[14] || (_cache[14] = _createElementVNode("div", { class: "divisor" }, null, -1)),
          _createElementVNode("div", _hoisted_18, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(hijosRaiz.value, (comp) => {
              return (_openBlock(), _createBlock(NodoComponente, {
                key: comp.id_componente,
                componente: comp,
                nivel: nivelBase.value,
                loading: loading.value,
                lockedMap: lockedMap.value,
                componentes: componentes.value,
                storeRaizId: _unref(storeComponentes).componentes[0]?.id_componente ?? null,
                obtenerNumeracion: obtenerNumeracionJerarquica,
                mostrarHijos: tipoFiltro.value !== 'solo-nivel-1',
                nivelMaximo: tipoFiltro.value === 'nivel-1-y-2' ? nivelBase.value + 1 : undefined,
                onLimitarDecimales: limitarDecimales,
                onRedistribuir: redistribuirPeso,
                onFormatear: formatearDosDecimales,
                onUpdatePeso: onUpdatePeso
              }, null, 8, ["componente", "nivel", "loading", "lockedMap", "componentes", "storeRaizId", "mostrarHijos", "nivelMaximo"]))
            }), 128))
          ]),
          _cache[15] || (_cache[15] = _createElementVNode("div", { class: "divisor" }, null, -1)),
          _createElementVNode("div", _hoisted_19, [
            _createElementVNode("button", {
              class: "btn salir",
              onClick: volver
            }, "Volver"),
            _createElementVNode("button", {
              class: "btn guardar",
              onClick: guardarCambios,
              disabled: loading.value
            }, _toDisplayString(loading.value ? 'Guardando...' : 'Guardar Cambios'), 9, _hoisted_20)
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})